import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PiggyBank, AlertTriangle, Clock3, FileText, Check, Pencil, Sparkles, ChevronDown } from "lucide-react";

const eur = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;
const QUOTES = ["Brouillon", "Envoyé", "Refusé"];
const PAID = ["Encaissé", "Payé"];
const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const today = () => new Date().toISOString().slice(0, 10);
const due = (a) => (a.expectedPaymentDate || a.endAt || a.startAt || "").slice(0, 10);

function Ring({ pct }) {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90" data-testid="treso-ring">
      <circle cx="60" cy="60" r={r} strokeWidth="12" className="stroke-secondary fill-none" />
      <circle cx="60" cy="60" r={r} strokeWidth="12" strokeLinecap="round" fill="none" stroke="url(#treso-grad)"
        strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(pct, 100) / 100)} style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.2,.8,.2,1)" }} />
      <defs><linearGradient id="treso-grad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#10B981" /><stop offset="100%" stopColor="#A855F7" /></linearGradient></defs>
    </svg>
  );
}

function Tile({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <div className={`rounded-2xl p-4 ${tone}`} data-testid={testid}>
      <Icon className="w-5 h-5" />
      <p className="font-heading text-lg sm:text-xl font-extrabold tabular-nums mt-2 whitespace-nowrap">{value}</p>
      <p className="text-xs font-semibold mt-0.5">{label}</p>
      {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function TreasuryHero({ showDetail, onToggleDetail }) {
  const [fin, setFin] = useState(null);
  const [appts, setAppts] = useState([]);
  const [hist, setHist] = useState([]);
  const [goal, setGoal] = useState(() => parseInt(localStorage.getItem("klt_ca_goal"), 10) || 10000);
  const [editGoal, setEditGoal] = useState(false);
  const [anim, setAnim] = useState(0);

  const load = () => {
    api.get("/appointments/finance").then(({ data }) => setFin(data)).catch(() => {});
    api.get("/appointments").then(({ data }) => setAppts(data || [])).catch(() => {});
    api.get("/appointments/finance/history", { params: { months: 6 } }).then(({ data }) => setHist(data.months || [])).catch(() => {});
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("birdie:changed", h); return () => window.removeEventListener("birdie:changed", h); }, []);

  const collected = fin?.collectedMonth || 0;
  const pct = goal ? Math.round((collected / goal) * 100) : 0;
  useEffect(() => { const t = setTimeout(() => setAnim(pct), 150); return () => clearTimeout(t); }, [pct]);

  const { upcoming, late, quotes } = useMemo(() => {
    const open = appts.filter((a) => (a.amount || 0) > 0 && !PAID.includes(a.status) && !QUOTES.includes(a.status));
    const t = today();
    return {
      upcoming: open.filter((a) => !due(a) || due(a) >= t).sort((x, y) => due(x).localeCompare(due(y))),
      late: open.filter((a) => due(a) && due(a) < t),
      quotes: appts.filter((a) => ["Brouillon", "Envoyé"].includes(a.status)),
    };
  }, [appts]);
  const sum = (l) => l.reduce((s, a) => s + (a.amount || 0) - (a.depositAmount || 0), 0);
  const max = Math.max(1, ...hist.map((m) => m.total));
  const left = Math.max(goal - collected, 0);
  const mood = pct >= 100 ? "Objectif pulvérisé, bravo ! 🚀" : pct >= 60 ? `Plus que ${eur(left)}, vous y êtes presque 💪` : `Encore ${eur(left)} pour atteindre votre objectif 🎯`;

  const collect = async (a) => {
    setAppts((l) => l.map((x) => (x.id === a.id ? { ...x, status: "Encaissé" } : x)));
    try {
      await api.patch(`/appointments/${a.id}`, { status: "Encaissé", paymentDate: today() });
      toast.success(`💰 +${eur((a.amount || 0) - (a.depositAmount || 0))} encaissés !`);
      load();
      window.dispatchEvent(new CustomEvent("birdie:changed"));
    } catch { toast.error("Encaissement impossible"); load(); }
  };
  const saveGoal = (v) => { const g = Math.max(100, parseInt(v, 10) || 10000); setGoal(g); localStorage.setItem("klt_ca_goal", String(g)); setEditGoal(false); };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-card p-6 shadow-[0_12px_40px_-18px_rgba(16,185,129,0.45)] klt-fade-up" data-testid="treso-hero">
        <div className="flex items-center gap-5">
          <div className="relative shrink-0">
            <Ring pct={anim} />
            <div className="absolute inset-0 flex flex-col items-center justify-center"><PiggyBank className="w-6 h-6 text-[#10B981]" /><span className="text-sm font-extrabold tabular-nums" data-testid="treso-pct">{pct}%</span></div>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Encaissé ce mois-ci</p>
            <p className="font-heading klt-num-xl klt-neon-text mt-1" data-testid="treso-collected">{eur(collected)}</p>
            {editGoal ? (
              <input type="number" autoFocus defaultValue={goal} onBlur={(e) => saveGoal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveGoal(e.target.value)} data-testid="treso-goal-input" className="mt-1 w-28 h-8 rounded-lg border border-input bg-background px-2 text-sm font-bold" />
            ) : (
              <button onClick={() => setEditGoal(true)} data-testid="treso-goal" className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">Objectif {eur(goal)} <Pencil className="w-3 h-3" /></button>
            )}
          </div>
        </div>
        <p className="mt-4 text-sm font-semibold flex items-center gap-1.5" data-testid="treso-mood"><Sparkles className="w-4 h-4 text-[#A855F7]" /> {mood}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Tile icon={Clock3} label="À encaisser (total)" value={eur(sum(upcoming))} sub={`${upcoming.length} projet${upcoming.length > 1 ? "s" : ""}`} tone="bg-sky-500/10 text-sky-800 dark:text-sky-200" testid="treso-tile-upcoming" />
        <Tile icon={AlertTriangle} label="En retard" value={eur(sum(late))} sub={late.length ? "à relancer !" : "rien en retard 👌"} tone={late.length ? "bg-orange-500/15 text-orange-800 dark:text-orange-200" : "bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"} testid="treso-tile-late" />
        <Tile icon={FileText} label="Devis en attente" value={eur(quotes.reduce((s, a) => s + (a.amount || 0), 0))} sub={`${quotes.length} devis`} tone="bg-violet-500/10 text-violet-800 dark:text-violet-200" testid="treso-tile-quotes" />
      </div>

      <div className="rounded-2xl bg-card p-5 shadow-[0_8px_30px_-16px_rgba(15,23,42,0.18)]" data-testid="treso-todo">
        <p className="font-heading font-bold">Prochains encaissements</p>
        <p className="text-xs text-muted-foreground">Un appui sur ✓ quand l'argent est arrivé</p>
        {[...late, ...upcoming].length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Aucun paiement en attente 🎉</p> : (
          <ul className="mt-3 space-y-2">
            {[...late, ...upcoming].slice(0, 6).map((a) => {
              const isLate = late.includes(a);
              return (
                <li key={a.id} className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3" data-testid={`treso-item-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{a.title}</p>
                    <p className={`text-xs ${isLate ? "text-orange-600 font-semibold" : "text-muted-foreground"}`}>{a.clientName || a.contactName} · {due(a) ? `${isLate ? "⚠️ prévu le " : "le "}${new Date(due(a)).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}` : "📅 date à fixer"}</p>
                  </div>
                  <span className="font-bold tabular-nums text-sm">{eur((a.amount || 0) - (a.depositAmount || 0))}</span>
                  <button onClick={() => collect(a)} data-testid={`treso-collect-${a.id}`} aria-label="Marquer encaissé" className="w-10 h-10 rounded-full bg-[#10B981] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"><Check className="w-5 h-5" /></button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-2xl bg-card p-5 shadow-[0_8px_30px_-16px_rgba(15,23,42,0.18)]" data-testid="treso-chart">
        <p className="font-heading font-bold">Vos 6 derniers mois</p>
        <div className="flex items-end gap-2 h-32 mt-4">
          {hist.map((m, i) => {
            const cur = i === hist.length - 1;
            return (
              <div key={m.key} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                <span className="text-[10px] font-semibold text-muted-foreground tabular-nums">{m.total ? `${Math.round(m.total / 1000)}k` : ""}</span>
                <div className={`w-full rounded-t-lg produit-bar ${cur ? "bg-gradient-to-t from-[#10B981] to-[#A855F7]" : "bg-[#10B981]/40"}`} style={{ height: `${Math.max(4, (m.total / max) * 100)}%`, animationDelay: `${i * 70}ms` }} />
                <span className={`text-[10px] ${cur ? "font-bold" : "text-muted-foreground"}`}>{MONTHS[parseInt(m.key.slice(5), 10) - 1]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <button onClick={onToggleDetail} data-testid="treso-detail-toggle" className="w-full h-11 rounded-2xl bg-secondary text-sm font-semibold inline-flex items-center justify-center gap-1.5">
        {showDetail ? "Masquer le détail comptable" : "Voir le détail comptable (registres, exports)"} <ChevronDown className={`w-4 h-4 transition-transform ${showDetail ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}
